import React, { useState } from 'react';
import { PostProcessingConfig, PostProcessingAction, PostProcessingFieldConfig } from '@/types/platform';
import './PostProcessing.css';

interface PostProcessingProps {
  config: PostProcessingConfig;
  spreadsheetDataAvailable: boolean;
  platformId: string;
  tableRows: string[][];
  getTranslation: (key: string) => string;
  onSubmit: (actionId: string, strategy: string, processingMode: { documentTypeOverride?: number | null }, values: Record<string, any>) => Promise<void>;
}

const PostProcessing: React.FC<PostProcessingProps> = ({
  config,
  spreadsheetDataAvailable,
  getTranslation,
  onSubmit,
}) => {
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const visibleActions = config.actions.filter(action => {
    if (!action.visible) return false;
    if (action.showWhen?.spreadsheetDataAvailable && !spreadsheetDataAvailable) return false;
    return true;
  });

  if (visibleActions.length === 0) return null;

  const openPanel = (action: PostProcessingAction) => {
    // Seed defaults
    const defaults: Record<string, any> = {};
    action.panel.fields.forEach(field => {
      if (field.defaultValue !== undefined) {
        defaults[field.name] = field.defaultValue === 'today'
          ? new Date().toISOString().split('T')[0]
          : field.defaultValue;
      } else {
        defaults[field.name] = '';
      }
    });
    setFieldValues(defaults);
    setSubmitError(null);
    setSubmitSuccess(false);
    setOpenActionId(action.id);
  };

  const closePanel = () => {
    setOpenActionId(null);
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleFieldChange = (name: string, value: any) => {
    setFieldValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (action: PostProcessingAction) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const strategy = action.panel.processingModes?.[0]?.strategy;
      if (!strategy) {
        console.error('PostProcessing: no strategy found on panel', action.panel);
        throw new Error('Processing strategy is not configured for this action.');
      }
      const processingMode = action.panel.processingModes![0];
      const convertedValues = { ...fieldValues };
      action.panel.fields.forEach(field => {
        if (field.type === 'date' && convertedValues[field.name]) {
          const [y, m, d] = convertedValues[field.name].split('-');
          convertedValues[field.name] = `${d}.${m}.${y}`;
        }
      });
      await onSubmit(action.id, strategy, processingMode, convertedValues);
      setSubmitSuccess(true);
    } catch (err: any) {
      setSubmitError(err?.message ?? 'An error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: PostProcessingFieldConfig) => {
    const label = getTranslation(field.labelKey);
    const placeholder = field.placeholderKey ? getTranslation(field.placeholderKey) : '';
    const value = fieldValues[field.name] ?? '';

    return (
      <div key={field.name} className="pp-field">
        <label className="pp-field-label">
          {label}
          {field.required && <span className="pp-required">*</span>}
        </label>
        <input
          className="pp-field-input"
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          value={value}
          placeholder={placeholder}
          required={field.required}
          min={field.min}
          step={field.step}
          onChange={e => handleFieldChange(field.name, e.target.value)}
        />
      </div>
    );
  };

  const openAction = visibleActions.find(a => a.id === openActionId) ?? null;

  return (
    <div className="post-processing">
      <div className="pp-actions">
        {visibleActions.map(action => (
          <button
            key={action.id}
            className={`pp-action-btn pp-action-btn--${action.color ?? 'primary'}`}
            onClick={() => openPanel(action)}
          >
            {getTranslation(action.labelKey)}
          </button>
        ))}
      </div>

      {openAction && (
        <div className="pp-panel">
          <div className="pp-panel-header">
            <div>
              <div className="pp-panel-title">{getTranslation(openAction.panel.titleKey)}</div>
              <div className="pp-panel-description">{getTranslation(openAction.panel.descriptionKey)}</div>
            </div>
            <button className="pp-panel-close" onClick={closePanel} aria-label="Close">✕</button>
          </div>

          {submitSuccess ? (
            <div className="pp-success-banner">
              <span className="pp-success-icon">✓</span>
              {getTranslation('postProcessing.convertToSale.successMessage')}
            </div>
          ) : (
            <>
              <div
                className="pp-fields-grid"
                style={{ gridTemplateColumns: `repeat(${openAction.panel.columns ?? 1}, 1fr)` }}
              >
                {openAction.panel.fields.map(renderField)}
              </div>

              {submitError && <div className="pp-error">{submitError}</div>}

              <div className="pp-panel-footer">
                <button className="pp-cancel-btn" onClick={closePanel} disabled={isSubmitting}>
                  {getTranslation('postProcessing.cancel')}
                </button>
                <button
                  className={`pp-submit-btn pp-submit-btn--${openAction.color ?? 'primary'}`}
                  disabled={isSubmitting}
                  onClick={() => handleSubmit(openAction)}
                >
                  {isSubmitting
                    ? getTranslation('postProcessing.submitting')
                    : getTranslation(openAction.panel.submitButtonLabelKey)}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PostProcessing;
